/**
 * Pure query-string builder for `GET /api/admin/venue-owners/:userId/payouts`.
 *
 * `status` is omitted unless set; `limit` is only emitted when it differs from
 * the API default (20) and is clamped to the API max (100). Returns an empty
 * string when nothing is set.
 *
 * Side-effect free — unit-tested in `owner-payouts-query.test.ts`.
 */
import { OWNER_PAYOUTS_LIMIT, OWNER_PAYOUTS_MAX_LIMIT } from "./constants";
import type { OwnerPayoutStatus } from "./types";

export interface OwnerPayoutsFilters {
  readonly status: OwnerPayoutStatus | null;
  readonly limit: number;
}

export function buildOwnerPayoutsQuery(filters: OwnerPayoutsFilters): string {
  const params = new URLSearchParams();

  if (filters.status) params.set("status", filters.status);

  if (Number.isFinite(filters.limit)) {
    // Normalize first, then compare to the default — so a fractional input
    // that truncates to the default (e.g. 20.5 → 20) is omitted too.
    const clamped = Math.min(Math.max(1, Math.trunc(filters.limit)), OWNER_PAYOUTS_MAX_LIMIT);
    if (clamped !== OWNER_PAYOUTS_LIMIT) params.set("limit", String(clamped));
  }

  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}
